import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { TemplateApi } from 'api';
import { getFiTemplateStore } from 'stores/FiTemplateStore';
import { BANK_TEMPLATES_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

export function useBankTemplates({ shouldNotifyOnChange = true }: { shouldNotifyOnChange?: boolean } = {}) {
  const templateApi = new TemplateApi();
  const bankTemplateStore = getFiTemplateStore();
  const { userBankId } = useUser();

  const { data: templates, isLoading: isLoadingTemplates } = useQuery({
    enabled: !!userBankId.length,
    queryKey: [BANK_TEMPLATES_KEY, userBankId],
    queryFn: () => templateApi.getBankTemplates(userBankId, true), // include archived templates
    ...(!shouldNotifyOnChange && { notifyOnChangeProps: [] })
  });

  const { onboardingWithoutArchived, questionnairesWithoutArchived } = useMemo(() => {
    if (!templates) return { onboardingWithoutArchived: {}, questionnairesWithoutArchived: {} };

    const onboardingWithoutArchived = Object.fromEntries(
      Object.entries(templates.onboarding || {}).filter(([, template]) => !template.archived)
    );
    const questionnairesWithoutArchived = Object.fromEntries(
      Object.entries(templates.questionnaires || {}).filter(([, template]) => !template.archived)
    );

    return { onboardingWithoutArchived, questionnairesWithoutArchived };
  }, [templates]);

  useEffect(() => {
    if (templates && Object.keys(templates).length) {
      bankTemplateStore.updateBankTemplates(templates);
      bankTemplateStore.updateActiveBankTemplates({
        ...templates,
        onboarding: onboardingWithoutArchived,
        questionnaires: questionnairesWithoutArchived
      });
    }
  }, [templates]);

  return { templates, onboardingWithoutArchived, questionnairesWithoutArchived, isLoadingTemplates };
}
